'use client';

/**
 * Add-credits modal — opened from the credit usage overlay's primary CTA,
 * behind the `usage-based-billing-on-dashboard` flag. Submitting records a
 * sales-assisted credit request (`requestWorkspaceCredits`); nothing is
 * charged or granted here, ops fulfil the request from admin within 24 hours,
 * which is why the confirmation says the request was received and credits will
 * be added, rather than "Credits added", and shows no dollar amount. A workspace
 * holds one pending request at a time, so while the credit summary reports one
 * the modal shows it in place of the preset picker.
 *
 * Built on `@mantine/core` per WEB-ADR-001 (ui-lib is deprecated), matching
 * `CreditUsageOverlay`. Mantine components used here: `Modal` (title + close
 * button), `List`/`Text` for the explainer bullets, `Anchor` for the inline
 * "Speak with sales" link, `SimpleGrid` for the preset row, `Alert` for a
 * failed submission, and `Button` for both the presets and the footer actions.
 * Content padding and the footer rule come from the shared
 * `@mantine-components/ModalContent` and `@mantine-components/ModalFooter`
 * wrappers, matching the other Mantine modals in the app.
 */

import { useMutation } from '@apollo/client/react';
import { faCoins } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RequestWorkspaceCreditsDocument } from '@graphql/mutations/credit-request/core.graphql';
import { GetWorkspaceCreditSummaryDocument } from '@graphql/queries/credit-summary/core.graphql';
import {
  Alert,
  Anchor,
  Button,
  Group,
  List,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { ModalContent } from '@mantine-components/ModalContent';
import { ModalFooter } from '@mantine-components/ModalFooter';
import { useEffect, useId, useRef, useState } from 'react';

import { trackAddCreditsViewed, trackCreditPackSelected } from './analytics';
import type { CreditSummary } from './derive-credit-summary-view';
import { presetButtonSelected } from './styles.css';
import { useCreditSummary } from './use-credit-summary';

/** Preset credit-pack amounts offered on the "Select amount" row. Mirrors the API's presets. */
export const CREDIT_PACK_OPTIONS = [250, 500, 1_000] as const;

/** The existing onboarding contact-sales form; unlimited plans are a sales conversation. */
const SPEAK_WITH_SALES_URL = '/onboarding/contact-sales';

/** Matches the 468px modal-instance width (Figma node `2421:18457`); the
 * standalone design frame's 847px includes surrounding canvas, not just the
 * dialog itself. */
const MODAL_WIDTH = '468px';

interface AddCreditsModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

interface CreditPackButtonProps {
  amount: (typeof CREDIT_PACK_OPTIONS)[number];
  selected: boolean;
  onSelect: () => void;
}

function CreditPackButton({ amount, selected, onSelect }: CreditPackButtonProps) {
  return (
    <Button
      className={selected ? presetButtonSelected : undefined}
      variant="default"
      role="radio"
      aria-checked={selected}
      h={48}
      fullWidth
      leftSection={<FontAwesomeIcon icon={faCoins} />}
      onClick={onSelect}
    >
      <Text fw={700} size="sm">
        {amount.toLocaleString('en-US')}
      </Text>
    </Button>
  );
}

/** What happens next for a request ops have not fulfilled yet. Deliberately no price. */
function FulfilmentNotice({ credits }: { credits: number }) {
  return (
    <Text size="sm" c="dimmed" ta="center">
      We will bill the payment method on file for the {credits.toLocaleString('en-US')} credits and
      add the credits to your account within 24 hours. You will receive a confirmation email once
      successfully added.
    </Text>
  );
}

/** Shown once the request is recorded. Deliberately no price or "added" wording. */
function RequestReceived({ credits }: { credits: number }) {
  return (
    <Stack gap="xs" align="center" py="md">
      <Text fw={600}>Your credit request has been received.</Text>
      <FulfilmentNotice credits={credits} />
    </Stack>
  );
}

/** Shown while an earlier request is still waiting on ops, in place of the presets. */
function RequestPending({ requestedCreditsMilli }: { requestedCreditsMilli: number }) {
  const credits = Math.round(requestedCreditsMilli / 1000);
  return (
    <Stack gap="xs" align="center" py="md">
      <Text fw={600}>
        You have a pending request for {credits.toLocaleString('en-US')} credits.
      </Text>
      <FulfilmentNotice credits={credits} />
    </Stack>
  );
}

/** Shown when the summary could not be read, so it is unknown whether a request is pending. */
function SummaryUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <Alert color="red" variant="light">
      <Stack gap="xs" align="flex-start">
        <Text size="sm">We couldn't check your credits just now.</Text>
        <Button size="xs" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    </Alert>
  );
}

/**
 * What the modal body shows. A request just sent wins; otherwise nothing is offered
 * until a fresh summary says whether a request is pending, because the cached one
 * may predate ops resolving it, and a failed read cannot say either way.
 */
type ModalView = 'received' | 'checking' | 'unavailable' | 'pending' | 'form';

function modalView({
  submitted,
  summaryLoading,
  summary,
}: {
  submitted: boolean;
  summaryLoading: boolean;
  summary: CreditSummary | null;
}): ModalView {
  if (submitted) {
    return 'received';
  }
  if (summaryLoading) {
    return 'checking';
  }
  if (!summary) {
    return 'unavailable';
  }
  return summary.pendingRequest ? 'pending' : 'form';
}

export function AddCreditsModal({ workspaceId, open, onClose }: AddCreditsModalProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const selectAmountLabelId = useId();
  const {
    loading: summaryLoading,
    summary,
    retry: refetchSummary,
  } = useCreditSummary(workspaceId, { skip: !open, revalidate: true });
  // Refetching the summary lets the dropdown and overlay pick up the new pending request.
  const [requestWorkspaceCredits, { loading }] = useMutation(RequestWorkspaceCreditsDocument, {
    refetchQueries: [GetWorkspaceCreditSummaryDocument],
  });
  const view = modalView({ submitted, summaryLoading, summary });
  const showDone = view === 'received' || view === 'pending';

  // Once per open, on the first view the summary settles on, so the funnel entry is what the
  // customer was actually offered rather than the loader.
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!open) {
      viewTracked.current = false;
      return;
    }
    if (viewTracked.current || view === 'checking' || view === 'received') {
      return;
    }
    viewTracked.current = true;
    trackAddCreditsViewed({ workspaceId, modalState: view });
  }, [open, view, workspaceId]);

  const handleClose = () => {
    onClose();
    setSelectedAmount(null);
    setSubmitted(false);
    setFailed(false);
  };

  const handleSubmit = async () => {
    if (selectedAmount === null) {
      return;
    }
    setFailed(false);
    try {
      await requestWorkspaceCredits({
        variables: { input: { workspaceId, credits: selectedAmount } },
      });
      setSubmitted(true);
    } catch {
      // Apollo already reports the error; the modal only needs to keep the form up.
      // A refetch swaps in the pending view if the rejection was an earlier request
      // the cached summary had not yet seen.
      setFailed(true);
      refetchSummary();
    }
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      size={MODAL_WIDTH}
      withCloseButton
      title={
        <Text fw={600} size="lg" lh="28px">
          Add more credits
        </Text>
      }
    >
      <ModalContent px="lg">
        {view === 'received' && selectedAmount !== null && (
          <RequestReceived credits={selectedAmount} />
        )}
        {view === 'pending' && summary?.pendingRequest && (
          <RequestPending requestedCreditsMilli={summary.pendingRequest.requestedCreditsMilli} />
        )}
        {(view === 'checking' || view === 'unavailable' || view === 'form') && (
          <Stack gap="lg">
            <Stack gap="xs">
              <Text fw={600} size="sm" lh="16px">
                How buying credits works
              </Text>
              <List size="sm">
                <List.Item>Use credits for in-person lead capture or bulk enrichment</List.Item>
                <List.Item>Credits last for 12 months from purchase before expiring</List.Item>
                <List.Item>Credits arrive within 24 hours of purchase</List.Item>
                <List.Item>
                  Want an unlimited lead package?{' '}
                  <Anchor
                    href={SPEAK_WITH_SALES_URL}
                    target="_blank"
                    rel="noopener"
                    inherit
                    fw={600}
                    underline="always"
                  >
                    Speak with sales
                  </Anchor>
                </List.Item>
              </List>
            </Stack>
            <Stack gap="xs">
              <Text fw={600} size="sm" lh="16px" id={selectAmountLabelId}>
                Select amount
              </Text>
              {/* Until a fresh summary says whether a request is pending, offering
                  presets would invite a submit the ledger then refuses. */}
              {view === 'checking' && (
                <Group justify="center" h={48}>
                  <Loader size="sm" />
                </Group>
              )}
              {view === 'unavailable' && <SummaryUnavailable onRetry={refetchSummary} />}
              {view === 'form' && (
                <SimpleGrid
                  cols={3}
                  spacing={8}
                  role="radiogroup"
                  aria-labelledby={selectAmountLabelId}
                >
                  {CREDIT_PACK_OPTIONS.map((amount) => (
                    <CreditPackButton
                      key={amount}
                      amount={amount}
                      selected={selectedAmount === amount}
                      onSelect={() => {
                        // Re-clicking the selected pack is not a new choice.
                        if (amount !== selectedAmount) {
                          trackCreditPackSelected({ workspaceId, credits: amount });
                        }
                        setSelectedAmount(amount);
                      }}
                    />
                  ))}
                </SimpleGrid>
              )}
            </Stack>
            {failed && (
              <Alert color="red" variant="light">
                We could not send your request just now. Please try again.
              </Alert>
            )}
          </Stack>
        )}
      </ModalContent>
      <ModalFooter>
        <Group justify="flex-end" gap="sm">
          {showDone ? (
            <Button size="md" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" size="md" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="md"
                onClick={handleSubmit}
                disabled={selectedAmount === null || view !== 'form'}
                loading={loading}
              >
                Add credits
              </Button>
            </>
          )}
        </Group>
      </ModalFooter>
    </Modal>
  );
}
